/*
 * carousel-last-slide-align.js  (v3 — self-healing wrapper)
 *
 * Problem: xo-carousel clamps activeIndex to [0, slideLength - perView]. With a
 * fractional perView (e.g. 1.3 to peek the next slide) the upper bound is
 * fractional, so the last reachable index leaves the final slide partway
 * across the viewport rather than aligned with slide 0 / 1, and with perMove=1
 * the LAST click only advances by `1 - (perView - floor(perView))` of a slide
 * width — the "ちょっとずつしか進まない" symptom users report.
 *
 * Approach (self-healing):
 *
 *   1. Wrap `window.xoStore.set` and, for any 'xo-carousel' state write that
 *      touches a carousel we should fix, recompute the correct slideLength
 *      from scratch:
 *        - fractional perView (e.g. 1.3): target = realLen + (perView - 1)
 *        - integer / <=1 perView:         target = realLen
 *      and rewrite `entry.slideLength` to that target. No `tracked` map — the
 *      DOM is the single source of truth, so any past state corruption (e.g.
 *      a leftover desktop value of `realLen + 0.3` after a breakpoint switch
 *      to mobile) self-corrects on the very next state write, no matter what
 *      the order of init / resize / breakpoint / clearTracked events was.
 *
 *   2. When perView is fractional, also keep an end-spacer of width
 *      `(perView - 1) * slideOuter` so that the carousel's
 *      `slideSize = scrollWidth / slideLength` keeps equalling one slide width
 *      (= each click advances exactly one slide). When perView turns integer
 *      we collapse the spacer to 0 so scrollWidth tracks the natural layout.
 *
 *   3. Capture-phase click guard runs `applyFix` synchronously on the clicked
 *      carousel before the carousel's own bubble-phase handler reads
 *      slideLength / slideSize, eliminating any initial-load race.
 *
 * Scoped to homepage product carousels (`.xo-coll-sidebar-carousel-unit`) and
 * the about heri:sync section that uses `[id*="section_PTMkqx"]`.
 */
(function () {
  'use strict';

  var SPACER_ATTR = 'data-last-slide-spacer';
  var setterPatched = false;
  // name -> 'fix' | 'noop' resolved from DOM. Cache to avoid repeated
  // querySelector + closest checks on every state write. Invalidated on
  // shopify:section:load and on DOM subtree changes.
  var fixCache = Object.create(null);

  function shouldFix(carousel) {
    if (!carousel) return false;
    if (carousel.hasAttribute('xo-loop')) return false;
    // Skip nested xo-carousel elements (e.g. product-image carousels inside
    // product cards). The closest('.xo-coll-sidebar-carousel-unit') check below
    // would otherwise match the OUTER unit for a nested carousel too.
    if (carousel.parentElement && carousel.parentElement.closest('xo-carousel')) return false;
    if (carousel.closest('[id*="section_PTMkqx"]')) return true;
    if (carousel.closest('.xo-coll-sidebar-carousel-unit')) return true;
    return false;
  }

  function outerList(carousel) {
    // The outer carousel often contains nested <xo-carousel> elements (e.g.
    // product-image carousels inside product cards). `querySelectorAll('xo-carousel-list > X')`
    // walks the whole subtree and matches X in nested lists too — it would
    // count the OUTER carousel as having (real slides) + (nested image slides)
    // children, inflating slideLength to absurd values. Scope to ONLY the
    // outer carousel's direct list with `:scope >`.
    return carousel.querySelector(':scope > xo-carousel-inner > xo-carousel-list');
  }

  function realSlideEls(carousel) {
    var list = outerList(carousel);
    if (!list) return [];
    return list.querySelectorAll(
      ':scope > xo-item:not([xo-cloned]),' +
      ' :scope > xo-carousel-slide:not([xo-cloned]),' +
      ' :scope > [xo-carousel-slide]:not([xo-cloned])'
    );
  }

  function lookupCarousel(name) {
    if (!name) return null;
    if (fixCache[name] === 'noop') return null;
    var c = document.querySelector('xo-carousel[xo-name="' + name + '"]');
    if (!c) return null;
    if (!shouldFix(c)) {
      fixCache[name] = 'noop';
      return null;
    }
    fixCache[name] = 'fix';
    return c;
  }

  function targetSlideLengthFor(realLen, perView) {
    if (typeof perView !== 'number' || perView <= 0) return realLen;
    // Fractional > 1: inflate so the clamp lands on an integer index.
    if (perView > 1 && Math.abs(perView - Math.round(perView)) >= 0.001) {
      return realLen + (perView - 1);
    }
    return realLen;
  }

  function patchSetter() {
    if (setterPatched) return;
    var store = window.xoStore;
    if (!store || typeof store.set !== 'function') return;
    var orig = store.set.bind(store);
    store.set = function (key, updater) {
      if (key !== 'xo-carousel' || typeof updater !== 'function') return orig(key, updater);
      return orig(key, function (prev) {
        var next = updater(prev);
        if (!next || typeof next !== 'object') return next;
        for (var name in next) {
          if (!Object.prototype.hasOwnProperty.call(next, name)) continue;
          var entry = next[name];
          if (!entry || typeof entry.slideLength !== 'number' || !entry.options) continue;
          var perView = entry.options.xoPerView;
          if (typeof perView !== 'number' || perView <= 0) continue;
          var carousel = lookupCarousel(name);
          if (!carousel) continue;
          var realLen = realSlideEls(carousel).length;
          if (realLen < 1) continue;
          var target = targetSlideLengthFor(realLen, perView);
          if (Math.abs(entry.slideLength - target) < 0.001) continue;
          // Clone entry to avoid mutating prev-shared references.
          var cloned = {};
          for (var k in entry) {
            if (Object.prototype.hasOwnProperty.call(entry, k)) cloned[k] = entry[k];
          }
          cloned.slideLength = target;
          next[name] = cloned;
        }
        return next;
      });
    };
    setterPatched = true;
  }

  function ensureSpacer(list) {
    var sp = list.querySelector(':scope > [' + SPACER_ATTR + ']');
    if (sp) return sp;
    sp = document.createElement('div');
    sp.setAttribute(SPACER_ATTR, '');
    sp.setAttribute('xo-cloned', '');
    sp.setAttribute('aria-hidden', 'true');
    sp.style.cssText = 'flex-shrink:0;flex-grow:0;align-self:stretch;pointer-events:none;visibility:hidden;width:0px;';
    list.appendChild(sp);
    return sp;
  }

  function measureSlideOuter(carousel, list, perView, slides) {
    if (slides && slides.length >= 2) {
      var firstRect = slides[0].getBoundingClientRect();
      var secondRect = slides[1].getBoundingClientRect();
      var dx = secondRect.left - firstRect.left;
      if (isFinite(dx) && dx > 0) return dx;
    }
    var listWidth = list.clientWidth;
    if (!listWidth) return 0;
    var cs = getComputedStyle(carousel);
    var gap = parseFloat(cs.getPropertyValue('--xo-gap'));
    if (!isFinite(gap)) gap = 0;
    return (listWidth + gap) / perView;
  }

  // Sync the spacer width and trigger a state-write so the wrapper
  // immediately rewrites slideLength to the correct target.
  function applyFix(carousel) {
    var store = window.xoStore;
    if (!store) return false;
    patchSetter();
    var name = carousel.xoName || carousel.getAttribute('xo-name');
    if (!name) return false;
    fixCache[name] = 'fix';
    var bag = store.get('xo-carousel') || {};
    var entry = bag[name];
    if (!entry || !entry.options) return false;
    var perView = entry.options.xoPerView;
    var listEl = outerList(carousel);
    if (!listEl) return false;
    var slides = realSlideEls(carousel);
    if (slides.length < 1) return false;

    var isFractional = typeof perView === 'number'
      && perView > 1
      && Math.abs(perView - Math.round(perView)) >= 0.001;

    var sp = ensureSpacer(listEl);
    if (isFractional) {
      var slideOuter = measureSlideOuter(carousel, listEl, perView, slides);
      if (!isFinite(slideOuter) || slideOuter <= 0) {
        // Layout not ready: leave any existing spacer width as-is (better than
        // resetting it to a bogus zero), and skip — schedule will retry.
        return false;
      }
      var spacerWidth = (perView - 1) * slideOuter;
      var newWidth = Math.max(0, Math.round(spacerWidth * 100) / 100) + 'px';
      if (sp.style.width !== newWidth) sp.style.width = newWidth;
    } else {
      if (sp.style.width !== '0px') sp.style.width = '0px';
    }

    // Force the wrapper to reconcile state.slideLength now.
    var realLen = slides.length;
    var target = targetSlideLengthFor(realLen, perView);
    if (Math.abs(entry.slideLength - target) > 0.001) {
      store.set('xo-carousel', function (prev) { return prev; });
    }
    return true;
  }

  function attach(carousel) {
    if (carousel.__lastSlideAlignAttached) return;
    carousel.__lastSlideAlignAttached = true;

    var rafToken = 0;
    function schedule() {
      if (rafToken) return;
      rafToken = requestAnimationFrame(function () {
        rafToken = 0;
        applyFix(carousel);
      });
    }

    carousel.addEventListener('xo:carousel:init', schedule);
    carousel.addEventListener('xo:carousel:change', schedule);
    carousel.addEventListener('xo:carousel:resize', schedule);
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('load', schedule);
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(schedule);
    }

    var list = outerList(carousel);
    if (list) {
      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].type === 'childList') { schedule(); break; }
        }
      }).observe(list, { childList: true });
      if (typeof ResizeObserver === 'function') {
        new ResizeObserver(schedule).observe(list);
      }
      list.querySelectorAll('img').forEach(function (img) {
        if (!img.complete) {
          img.addEventListener('load', schedule, { once: true });
          img.addEventListener('error', schedule, { once: true });
        }
      });
    }

    schedule();
    setTimeout(schedule, 50);
    setTimeout(schedule, 250);
    setTimeout(schedule, 800);
    setTimeout(schedule, 1500);
    setTimeout(schedule, 3000);
  }

  function scan() {
    patchSetter();
    fixCache = Object.create(null); // re-evaluate on each scan in case of section reload
    var carousels = document.querySelectorAll('xo-carousel');
    for (var i = 0; i < carousels.length; i++) {
      if (shouldFix(carousels[i])) attach(carousels[i]);
    }
  }

  function carouselFromClick(el) {
    var direct = el.closest && el.closest('xo-carousel');
    if (direct) return direct;
    var sectionEl = el.closest && el.closest('[xo-section-id]');
    var sectionId = sectionEl
      ? sectionEl.getAttribute('xo-section-id')
      : (el.closest && el.closest('.shopify-section') || { id: '' }).id.replace(/^shopify-section-/, '');
    if (!sectionId) return null;
    return document.querySelector('xo-carousel[xo-name="' + sectionId + '"]');
  }

  function clickGuard(event) {
    var t = event.target;
    if (!t || !t.closest) return;
    var btn = t.closest('xo-carousel-prev, xo-carousel-next');
    if (!btn) return;
    var carousel = carouselFromClick(btn);
    if (!carousel || !shouldFix(carousel)) return;
    if (!carousel.__lastSlideAlignAttached) attach(carousel);
    applyFix(carousel);
  }

  // Install hooks at script load — xo-webcomponents.min.js (also defer'd) has
  // already created window.xoStore by the time this IIFE runs, but its
  // carousels' connectedCallback may have already issued setSlideLength(N).
  // The wrapper recomputes from DOM on every subsequent state write, so the
  // first nextSlide / goToSlide / setOptions write reconciles slideLength
  // before any user interaction.
  patchSetter();
  document.addEventListener('pointerdown', clickGuard, true);
  document.addEventListener('click', clickGuard, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  document.addEventListener('shopify:section:load', scan);
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
})();
